import { ChangeDetectionStrategy, Component, OnDestroy, signal, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { isJsonObject } from '@se/cp.common';
import {
    SeConstraintApplierService,
    SeConstraintConfiguratorService,
    SeConstraintNotifierService,
} from '@se/se.platform.base';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { flatten, isFunction, uniq } from 'lodash-es';
import { first, Subscription } from 'rxjs';
import { ComboCellEditorPopupComponent } from './combo-cell-editor-popup/combo-cell-editor-popup.component';
/**
 * Editor Custom AgGrid for Seelct Input.
 *
 * Params:
 * - dataSource: Array<any> (Mandatory) it defines option list, can be string or object array.
 * - viewValueFieldName: (Mandatory when datasource is Array<object>) it allows to specify which object property to show in drop down list.
 * - valueFieldName. (Optional) it allows to specify which object property to give back(ngModel).If it is null or undefined, the component gives back all the object.
 * - multi: boolean (Optional default value false) it allows multi selection.
 * - required: (Optional) It allows to specify required validator
 * - isResettable: (Optional) It allows to reset the cell value
 */
export interface IComboCelleEditorParams {
    required?: boolean;
    inlineEditor?: boolean;
    idDataSource?: string;
    dataSource: any[] | (() => any[]);
    viewValueFieldName?: string;
    valueFieldName?: string;
    viewFieldName?: string;
    multi?: boolean;
    isResettable?: boolean;
    configuratorService?: SeConstraintConfiguratorService;
    constraintFilterService?: SeConstraintApplierService;
    constraintNotifier?: SeConstraintNotifierService;
}
export type ComboCellEditorParams = ICellEditorParams & IComboCelleEditorParams;

const requiredMulti = (c: AbstractControl<any[] | null>) =>
    Array.isArray(c.value) && c.value.length > 0 ? null : { required: true };
@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'se-combo-cell-editor',
    templateUrl: './combo-cell-editor.component.html',
    styleUrls: ['./combo-cell-editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ComboCellEditorPopupComponent, ReactiveFormsModule],
})
export class ComboCellEditorComponent implements ICellEditorAngularComp, OnDestroy {
    // i parametri di input si recuperano in agInit da params es, params['max']
    readonly dataSource = signal<any[]>([]);
    readonly viewValueFieldName = signal<string>('');
    readonly valueFieldName = signal<string>('');
    readonly multi = signal<boolean>(false);
    readonly isResettable = signal<boolean>(false);
    readonly cellWidth = signal<string>('');
    readonly cellReady = signal<boolean>(false);
    readonly formControl = new FormControl<any | any[] | null>(null);

    private configuratorService: SeConstraintConfiguratorService | null = null;
    private constraintFilterService: SeConstraintApplierService | null = null;
    private constraintNotifier: SeConstraintNotifierService | null = null;

    private subscriptions: Subscription[] = [];

    handleFilterNotification = (constraintId: string) => {
        if (!this.constraintFilterService) return;
        this.dataSource.set(this.constraintFilterService.applyFilter(constraintId) ?? []);
    };

    agInit(params: ComboCellEditorParams): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
        this.subscriptions = [];
        this.formControl.clearValidators();
        this.formControl.setErrors(null);
        this.cellWidth.set(params.column.getActualWidth() + 'px');
        this.isResettable.set(!!params.isResettable);
        this.multi.set(!!params.multi);
        if (params.required) {
            this.formControl.addValidators(params.multi ? requiredMulti : Validators.required);
        }
        this.formControl.setValue(params.value ?? null, { emitEvent: false });
        this.formControl.updateValueAndValidity({ emitEvent: false });
        const dataSource = isFunction(params.dataSource) ? params.dataSource() : params.dataSource;
        if (!Array.isArray(dataSource)) {
            console.error('dataSource is not Array: ', this.dataSource);
        } else {
            this.dataSource.set(dataSource);
            const paramsViewValueFieldName = params.viewValueFieldName;
            const viewValueFieldName =
                paramsViewValueFieldName &&
                dataSource.every((item) => isJsonObject(item) && item.hasOwnProperty(paramsViewValueFieldName))
                    ? paramsViewValueFieldName
                    : '';
            this.viewValueFieldName.set(viewValueFieldName);
            const paramsValueFieldName = params.valueFieldName;
            const valueFieldName =
                paramsValueFieldName &&
                dataSource.every((item) => isJsonObject(item) && item.hasOwnProperty(paramsValueFieldName))
                    ? paramsValueFieldName
                    : '';
            this.valueFieldName.set(valueFieldName);
        }

        if (!!params.configuratorService) this.configuratorService = params.configuratorService;
        if (!!params.constraintFilterService) this.constraintFilterService = params.constraintFilterService;
        if (!!params.constraintNotifier) this.constraintNotifier = params.constraintNotifier;

        if (!!this.configuratorService && !!this.constraintNotifier) {
            const constraints = uniq(
                flatten(
                    this.configuratorService.constraintSources
                        .getSourcesByListName(
                            params.idDataSource ?? (params.colDef.cellEditorParams as any)?.idDataSource ?? '',
                        )
                        .map((s) => s.constraints),
                ),
            );

            const dataBindingContext = params.data?.path?.toString?.() ?? '';

            for (const c of constraints) {
                this.subscriptions.push(
                    this.constraintNotifier
                        .register(c.id, dataBindingContext)
                        .pipe(first())
                        .subscribe((data) => this.handleFilterNotification(data.constraint.id)),
                );
            }
        }
    }

    getValue(): any {
        return this.formControl.valid ? this.formControl.getRawValue() : null;
    }

    isPopup(): boolean {
        return true;
    }

    afterGuiAttached(): void {
        this.cellReady.set(true);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
